import React from "react";
import Container from "../../components/Container";
import Header from "../../components/Header";
import ProductDocument from "../../components/ProductDocument";
import Document from "../../components/Document";
import { Image } from "@clarkinc/zest-design-system/ui";
import { BaseResourceAndDownloadsProps } from "../../types";
import ChildOrNull from "~/common/ChildOrNull";
import {
  getDocumentLink,
  getDocumentThumbnail,
  getDocumentTypeLabel,
  getDocumentTitle,
  fixDocumentImage,
} from "../../utils";

const PdpResourcesAndDownloads: React.FC<BaseResourceAndDownloadsProps> = ({
  titleText,
  hasManufacturerText,
  manufacturerText = "",
  hasProductDocuments,
  productDocuments = [],
  hasWarrantyInformation,
  warrantyInformationText = "",
  hasLinkedResources,
  linkedResources = [],
}) => {
  return (
    <Container>
      <Header
        as="h3"
        data-testid="ResourcesAndDownloadsHeader"
        id="resources-group-header"
        className="font-bold mb-3 xm:mb-[15px] xm:font-semibold"
      >
        {titleText}
        {hasManufacturerText && (
          <>
            &nbsp;for <br aria-hidden="true" />
            {manufacturerText}
          </>
        )}
      </Header>
      <ChildOrNull condition={hasProductDocuments}>
        <ProductDocument className="w-full grid grid-cols-3 gap-x-[7.5%] gap-y-5 mb-5 text-center">
          {productDocuments?.map((doc) => (
            <Document
              title={getDocumentTitle(doc)}
              link={getDocumentLink(doc)}
              isExternal={!!doc.externalUrl}
              key={getDocumentTitle(doc)}
            >
              <Image
                src={getDocumentThumbnail(doc)}
                alt={getDocumentTitle(doc)}
                title={getDocumentTitle(doc)}
                onError={fixDocumentImage}
                loading="lazy"
                className="h-auto w-full border border-solid border-gray-200 shadow-[inset_0_1px_0_rgba(255,255,255,0.3),_0_1px_2px_rgba(0,0,0,0.1)] hover:border-gray-400"
                height={132}
                width={102}
              />
              <p className="mt-[5px] mb-0 text-[13px] leading-none">
                {getDocumentTypeLabel(doc)}
              </p>
              <span className="sr-only">Opens in new tab</span>
            </Document>
          ))}
        </ProductDocument>
        <p className="clear-both mb-0 text-xs -mt-2 xm:text-[0.7em] xm:leading-[1.4em] xm:mt-0">
          A PDF viewer is required to view this product&apos;s information.{" "}
          <a
            target="adobe"
            href="https://get.adobe.com/reader/"
            rel="nofollow"
            title="Download Adobe Reader"
            className="underline"
          >
            Download Adobe Acrobat software
            <span className="sr-only">Opens in new tab</span>
          </a>
        </p>
      </ChildOrNull>
      <ChildOrNull condition={hasWarrantyInformation}>
        <div className="resources warranty-info mt-3 xm:mt-5">
          <Header
            as="h4"
            className="header-4 text-base leading-tight mb-2 xm:mb-[15px] xm:text-sm"
          >
            Warranty Info
          </Header>
          <p
            className="mb-0 text-sm leading-tight"
            dangerouslySetInnerHTML={{
              __html: warrantyInformationText,
            }}
          />
        </div>
      </ChildOrNull>
      <ChildOrNull condition={hasLinkedResources}>
        <div className="resources mt-3 xm:mt-[15px]">
          <Header className="header-4" as="h4">
            Resources
          </Header>
          <ul className="m-0">
            {linkedResources?.map((resource) => (
              <li key={resource.title} className="item block">
                <i className="icon-book-open" />
                <a
                  className="description text-sm leading-[18px] xm:text-[0.9em] xm:leading-[1.2em]"
                  href={resource.link}
                  title={resource.title}
                  target="_blank"
                >
                  {resource.title}
                  <span className="sr-only">Opens in new tab</span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </ChildOrNull>
    </Container>
  );
};

export default PdpResourcesAndDownloads;
